<template>
  <div class="quick-add-station">
    <!-- 模式切换 -->
    <div class="mode-switcher">
      <el-radio-group v-model="addMode" @change="onModeChange">
        <el-radio-button value="single">单个添加</el-radio-button>
        <el-radio-button value="batch">批量添加</el-radio-button>
      </el-radio-group>
    </div>

    <!-- 单个添加模式 -->
    <el-form
      v-if="addMode === 'single'"
      ref="singleFormRef"
      :model="singleForm"
      :rules="singleRules"
      label-width="80px"
      @submit.prevent="submitSingleForm"
    >
      <el-form-item label="车站名称" prop="chinese_name">
        <el-input
          v-model="singleForm.chinese_name"
          placeholder="请输入中文名称"
          @input="onChineseNameChange"
          @blur="generateEnglishName"
        />
      </el-form-item>
      
      <el-form-item label="英文名称" prop="english_name">
        <el-input
          v-model="singleForm.english_name"
          placeholder="自动生成或手动输入"
        >
          <template #append>
            <el-button 
              @click="generateEnglishName" 
              :icon="Refresh"
              title="重新生成英文名"
            />
          </template>
        </el-input>
      </el-form-item>
      
      <!-- 快速添加说明 -->
      <el-alert
        title="快速添加车站"
        description="快速添加车站只需要输入名称和选择线路，无需输入坐标。添加后的车站会显示在线路列表中，但不会被导出到BlueMap配置中。"
        type="info"
        :closable="false"
        style="margin-bottom: 16px;"
      />

      <el-form-item label="中心坐标">
        <el-input
          v-model="singleForm.center_raw"
          placeholder="例如：123 456 或 123 64 456（可选）"
        />
        <div style="font-size: 12px; color: rgba(100, 116, 139, 0.95); line-height: 1.2; margin-top: 4px;">
          X、Z 必填；Y 可不填（默认 64）。留空则不在地图显示，但仍可加入线路。
        </div>
      </el-form-item>
      
      <el-form-item label="所属线路" prop="line_ids">
        <el-select
          v-model="singleForm.line_ids"
          multiple
          placeholder="选择线路"
          style="width: 100%;"
          filterable
          clearable
        >
          <el-option
            v-for="line in lines"
            :key="line.id"
            :label="line.line_name"
            :value="line.id"
          >
            <div style="display: flex; align-items: center;">
              <span
                style="width: 12px; height: 12px; border-radius: 2px; margin-right: 8px;"
                :style="{ backgroundColor: `rgb(${line.line_color_r}, ${line.line_color_g}, ${line.line_color_b})` }"
              ></span>
              {{ line.line_name }}
            </div>
          </el-option>
        </el-select>
      </el-form-item>
      
      <el-form-item label="描述">
        <el-input
          v-model="singleForm.description"
          type="textarea"
          :rows="2"
          placeholder="可选的车站描述信息"
        />
      </el-form-item>
      
      <div class="action-buttons">
        <el-button @click="$emit('cancel')">取消</el-button>
        <el-button type="primary" @click="submitSingleForm" :loading="loading">
          添加车站
        </el-button>
      </div>
    </el-form>

    <!-- 批量添加模式 -->
    <div v-if="addMode === 'batch'" class="batch-add-mode">
      <!-- 批量添加说明 -->
      <el-alert
        title="批量添加车站"
        description="每行输入一个车站的中文名称，系统会自动生成英文名称并显示在右侧。最后选择一条线路，所有车站都会添加到该线路中。"
        type="info"
        :closable="false"
        style="margin-bottom: 16px;"
      />

      <!-- 车站列表输入 -->
      <div class="station-list-editor">
        <div class="editor-header">
          <span class="header-title">车站列表</span>
          <div class="header-actions">
            <el-button size="small" @click="addStationRow">添加行</el-button>
            <el-button size="small" @click="regenerateAllEnglishNames">重新生成英文名</el-button>
            <el-button size="small" @click="clearAllStations">清空</el-button>
          </div>
        </div>
        
        <div class="station-rows">
          <div 
            v-for="(station, index) in batchStations" 
            :key="index"
            class="station-row"
          >
            <div class="row-number">{{ index + 1 }}</div>
            <div class="row-chinese">
              <el-input
                v-model="station.chinese_name"
                placeholder="输入车站中文名称"
                @input="generateBatchEnglishName(index)"
                size="small"
              />
            </div>
            <div class="row-english">
              <el-input
                v-model="station.english_name"
                placeholder="自动生成或手动输入英文名"
                size="small"
              />
            </div>
            <div class="row-actions">
              <el-button 
                size="small" 
                type="danger" 
                :icon="Delete"
                @click="removeStationRow(index)"
                :disabled="batchStations.length <= 1"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- 线路选择 -->
      <el-form-item label="所属线路" prop="batchLineId" style="margin-top: 20px;">
        <el-select
          v-model="batchLineId"
          placeholder="选择线路（所有车站将添加到此线路）"
          style="width: 100%;"
          filterable
          clearable
        >
          <el-option
            v-for="line in lines"
            :key="line.id"
            :label="line.line_name"
            :value="line.id"
          >
            <div style="display: flex; align-items: center;">
              <span
                style="width: 12px; height: 12px; border-radius: 2px; margin-right: 8px;"
                :style="{ backgroundColor: `rgb(${line.line_color_r}, ${line.line_color_g}, ${line.line_color_b})` }"
              ></span>
              {{ line.line_name }}
            </div>
          </el-option>
        </el-select>
      </el-form-item>

      <div class="action-buttons">
        <el-button @click="$emit('cancel')">取消</el-button>
        <el-button 
          type="primary" 
          @click="submitBatchForm" 
          :loading="loading"
          :disabled="!batchLineId || !batchStations.length || batchStations.every(s => !s.chinese_name.trim())"
        >
          批量添加 ({{ batchStations.filter(s => s.chinese_name.trim()).length }}个车站)
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import { Refresh, Delete } from '@element-plus/icons-vue'
import { stationApi, lineApi } from '../api'
import pinyin from 'pinyin'

export default {
  name: 'QuickAddStation',
  emits: ['success', 'cancel'],
  setup(props, { emit }) {
    const singleFormRef = ref()
    const loading = ref(false)
    const lines = ref([])
    const addMode = ref('single') // 'single' | 'batch'
    const QUICK_DEFAULT_OPERATION_STATUS = 3
    const QUICK_DEFAULT_CENTER_Y = 64
    
    // 单个添加表单数据
    const singleForm = reactive({
      chinese_name: '',
      english_name: '',
      center_raw: '',
      center_x: null,
      center_y: null,
      center_z: null,
      line_ids: [],
      description: '',
      shape_coordinates: [],
      shape_y: null,
      depth_test: false,
      is_quick_added: true
    })
    
    // 单个添加表单验证规则
    const singleRules = {
      chinese_name: [
        { required: true, message: '请输入车站名称', trigger: 'blur' }
      ],
      line_ids: [
        { required: true, message: '请至少选择一条线路', trigger: 'change' }
      ]
    }

    // 批量添加相关数据
    const batchStations = ref([
      { chinese_name: '', english_name: '' }
    ])
    const batchLineId = ref(null)
    
    // 生成英文名称（单个）
    const generateEnglishName = () => {
      if (singleForm.chinese_name) {
        const pinyinArray = pinyin(singleForm.chinese_name, {
          style: pinyin.STYLE_NORMAL,
          heteronym: false
        })
        
        // EnglishName开头首字母大写，其余小写
        let englishName = pinyinArray
          .map(py => py[0])
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join('')
        englishName = englishName.charAt(0).toUpperCase() + englishName.slice(1).toLowerCase()
        
        singleForm.english_name = englishName
      }
    }

    // 生成批量英文名称
    const generateBatchEnglishName = (index) => {
      const station = batchStations.value[index]
      if (station.chinese_name) {
        // 只有当英文名称为空时才自动生成，避免覆盖用户手动输入的内容
        if (!station.english_name || station.english_name.trim() === '') {
          const pinyinArray = pinyin(station.chinese_name, {
            style: pinyin.STYLE_NORMAL,
            heteronym: false
          })
          
          // EnglishName开头首字母大写，其余小写
          let englishName = pinyinArray
            .map(py => py[0])
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join('')
          englishName = englishName.charAt(0).toUpperCase() + englishName.slice(1).toLowerCase()
          
          station.english_name = englishName
        }
      } else {
        // 如果中文名称为空，清空英文名称
        station.english_name = ''
      }
    }

    // 中文名称变化时的处理（单个）
    const onChineseNameChange = () => {
      if (singleForm.chinese_name && !singleForm.english_name) {
        generateEnglishName()
      }
    }

    // 模式切换
    const onModeChange = (mode) => {
      if (mode === 'batch' && batchStations.value.length === 0) {
        batchStations.value = [{ chinese_name: '', english_name: '' }]
      }
    }

    // 添加车站行
    const addStationRow = () => {
      batchStations.value.push({ chinese_name: '', english_name: '' })
    }

    // 移除车站行
    const removeStationRow = (index) => {
      if (batchStations.value.length > 1) {
        batchStations.value.splice(index, 1)
      }
    }

    // 重新生成所有英文名称
    const regenerateAllEnglishNames = () => {
      batchStations.value.forEach((station) => {
        if (station.chinese_name && station.chinese_name.trim()) {
          const pinyinArray = pinyin(station.chinese_name, {
            style: pinyin.STYLE_NORMAL,
            heteronym: false
          })
          
          // EnglishName开头首字母大写，其余小写
          let englishName = pinyinArray
            .map(py => py[0])
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join('')
          englishName = englishName.charAt(0).toUpperCase() + englishName.slice(1).toLowerCase()
          
          station.english_name = englishName
        }
      })
    }

    // 清空所有车站
    const clearAllStations = () => {
      batchStations.value = [{ chinese_name: '', english_name: '' }]
      batchLineId.value = null
    }
    
    // 加载线路列表
    const loadLines = async () => {
      try {
        const result = await lineApi.getAll()
        lines.value = result.data
      } catch (error) {
        console.error('加载线路列表失败:', error)
        ElMessage.error('加载线路列表失败')
      }
    }

    const parseQuickCenterInput = (raw) => {
      const trimmed = (raw || '').trim()
      if (!trimmed) return null

      const parts = trimmed
        .replace(/[，]/g, ',')
        .split(/[,\s]+/)
        .map(part => part.trim())
        .filter(Boolean)

      if (parts.length !== 2 && parts.length !== 3) {
        throw new Error('中心坐标格式应为：x z 或 x y z')
      }

      const numbers = parts.map(part => Number(part))
      if (numbers.some(value => !Number.isFinite(value) || !Number.isInteger(value))) {
        throw new Error('中心坐标必须为整数')
      }

      if (numbers.length === 2) {
        return { x: numbers[0], y: QUICK_DEFAULT_CENTER_Y, z: numbers[1] }
      }

      return { x: numbers[0], y: numbers[1], z: numbers[2] }
    }
    
    // 提交单个表单
    const submitSingleForm = async () => {
      if (!singleFormRef.value) return
      
      try {
        await singleFormRef.value.validate()
        loading.value = true

        let center = null
        try {
          center = parseQuickCenterInput(singleForm.center_raw)
        } catch (error) {
          ElMessage.error(error.message || '中心坐标格式不正确')
          return
        }
        
        const quickStationData = {
          chinese_name: singleForm.chinese_name,
          english_name: singleForm.english_name,
          center_x: center ? center.x : null,
          center_y: center ? center.y : null,
          center_z: center ? center.z : null,
          shape_coordinates: [],
          shape_y: null,
          description: singleForm.description || `${singleForm.chinese_name}${singleForm.english_name ? '|' + singleForm.english_name : ''}`,
          depth_test: false,
          line_ids: singleForm.line_ids,
          is_quick_added: true,
          operation_status: QUICK_DEFAULT_OPERATION_STATUS
        }
        
        await stationApi.create(quickStationData)
        ElMessage.success('快速添加车站成功')
        emit('success')
      } catch (error) {
        console.error('添加车站失败:', error)
        ElMessage.error('添加车站失败: ' + (error.response?.data?.message || error.message))
      } finally {
        loading.value = false
      }
    }

    // 提交批量表单
    const submitBatchForm = async () => {
      if (!batchLineId.value) {
        ElMessage.error('请选择线路')
        return
      }

      const validStations = batchStations.value.filter(s => s.chinese_name.trim())
      if (validStations.length === 0) {
        ElMessage.error('请输入至少一个车站名称')
        return
      }

      try {
        loading.value = true
        
        const batchData = {
          stations: validStations.map(station => ({
            chinese_name: station.chinese_name.trim(),
            english_name: station.english_name.trim(),
            center_x: null,
            center_y: null,
            center_z: null,
            shape_coordinates: [],
            shape_y: null,
            description: `${station.chinese_name.trim()}${station.english_name.trim() ? '|' + station.english_name.trim() : ''}`,
            depth_test: false,
            line_ids: [batchLineId.value],
            is_quick_added: true,
            operation_status: QUICK_DEFAULT_OPERATION_STATUS
          })),
          line_id: batchLineId.value
        }
        
        await stationApi.createBatch(batchData)
        ElMessage.success(`成功批量添加 ${validStations.length} 个车站`)
        emit('success')
      } catch (error) {
        console.error('批量添加车站失败:', error)
        ElMessage.error('批量添加车站失败: ' + (error.response?.data?.message || error.message))
      } finally {
        loading.value = false
      }
    }
    
    onMounted(() => {
      loadLines()
    })
    
    return {
      singleFormRef,
      loading,
      lines,
      addMode,
      singleForm,
      singleRules,
      batchStations,
      batchLineId,
      generateEnglishName,
      generateBatchEnglishName,
      onChineseNameChange,
      onModeChange,
      addStationRow,
      removeStationRow,
      regenerateAllEnglishNames,
      clearAllStations,
      submitSingleForm,
      submitBatchForm,
      // Icons
      Refresh,
      Delete
    }
  }
}
</script>

<style scoped>
.quick-add-station {
  padding: 20px;
}

.mode-switcher {
  margin-bottom: 20px;
  text-align: center;
}

.mode-switcher .el-radio-group {
  background: #f5f7fa;
  padding: 4px;
  border-radius: 8px;
}

.batch-add-mode {
  padding: 16px 0;
}

.station-list-editor {
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  background: #fafafa;
  overflow: hidden;
}

.editor-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-weight: 600;
}

.header-title {
  font-size: 16px;
}

.header-actions {
  display: flex;
  gap: 8px;
}

.station-rows {
  padding: 16px;
}

.station-row {
  display: grid;
  grid-template-columns: 40px 1fr 1fr 60px;
  gap: 12px;
  align-items: center;
  margin-bottom: 12px;
  padding: 8px;
  background: white;
  border-radius: 6px;
  border: 1px solid #e4e7ed;
  transition: all 0.2s ease;
}

.station-row:hover {
  border-color: rgba(255, 138, 76, 0.5);
  box-shadow: 0 6px 18px rgba(255, 138, 76, 0.18);
}

.station-row:last-child {
  margin-bottom: 0;
}

.row-number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, #ff9f6b 0%, #ff6f61 100%);
  color: white;
  border-radius: 50%;
  font-weight: 600;
  font-size: 14px;
}

.row-chinese,
.row-english {
  min-width: 0;
}

.row-actions {
  display: flex;
  justify-content: center;
}

.action-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .quick-add-station {
    padding: 16px;
  }
  
  .station-row {
    grid-template-columns: 30px 1fr 1fr 50px;
    gap: 8px;
    padding: 6px;
  }
  
  .row-number {
    width: 28px;
    height: 28px;
    font-size: 12px;
  }
  
  .editor-header {
    padding: 10px 12px;
    flex-direction: column;
    gap: 8px;
    text-align: center;
  }
  
  .header-title {
    font-size: 14px;
  }
  
  .header-actions {
    justify-content: center;
  }
  
  .station-rows {
    padding: 12px;
  }
}

@media (max-width: 480px) {
  .station-row {
    grid-template-columns: 1fr;
    gap: 8px;
    text-align: center;
  }
  
  .row-number {
    justify-self: center;
  }
  
  .action-buttons {
    flex-direction: column;
    gap: 8px;
  }
  
  .action-buttons .el-button {
    width: 100%;
  }
}
</style>
